import 'vuetify/styles';
import { createVuetify } from 'vuetify';
import * as components from 'vuetify/components';
import { VTreeview } from 'vuetify/components';

import { mdi } from 'vuetify/iconsets/mdi';

import { md } from 'vuetify/iconsets/md';
import { icons } from '@/plugins/icons';

import rtlsIcons from '@rtls/rtls-ui-components/src/icons';
import owColors from '@/plugins/ow-colors';
import { STYLE_SCOPE_CLASS } from '@/webshell-embed/embedded';

// helper to take name of component from the path
const pathToComponentName = path =>
  path
    .split('/')
    .pop()
    .replace(/\.\w+$/, '');

const vuetify = createVuetify({
  components: { VTreeview },
  aliases: { RtlsSearch: components.VTextField },
  // directives,
  defaults: {
    // Overlays (dialogs, menus, tooltips) teleport to <body> by default, which embedded is outside
    // AO's CSS scope and leaves them unstyled. The scope class is on <body> standalone, so this
    // changes nothing there.
    global: { attach: `.${STYLE_SCOPE_CLASS}` },
    RtlsSearch: {
      appendInnerIcon: 'mdi-magnify',
      bgColor: 'white',
      density: 'compact',
      variant: 'filled',
      rounded: 0,
      autocomplete: 'off',
      flat: true,
      hideDetails: true,
      width: 240,
      class: 'rtls-search',
    },
    VRow: {
      style: {
        marginLeft: '0px',
        marginRight: '0px',
        marginTop: '4px',
        marginBottom: '4px',
      },
    },
    VAutocomplete: {
      density: 'compact',
      variant: 'filled',
      rounded: 0,
      flat: true,
      autocomplete: 'off',
      listProps: { density: 'compact' },
      menuProps: {
        // open all dropdowns down
        location: 'bottom',
      },
      class: 'rtls-autocomplete',
    },
    VCheckbox: {
      density: 'compact',
      hideDetails: true,
      class: 'rtls-checkbox',
    },
    VCombobox: {
      density: 'compact',
      variant: 'filled',
      rounded: 0,
      autocomplete: 'off',
      flat: true,
      listProps: { density: 'compact' },
      menuProps: {
        // open all dropdowns down
        location: 'bottom',
      },
    },
    VSelect: {
      density: 'compact',
      variant: 'filled',
      rounded: 0,
      flat: true,
      listProps: { density: 'compact' },
      menuProps: {
        // open all dropdowns down
        location: 'bottom',
      },
      class: 'rtls-text-field',
    },
    VSelectionControl: {
      color: 'primary',
      style: 'font-size: 16px;',
    },
    VRadio: { color: 'primary' },
    VLabel: { style: 'font-size: 12px;' },
    VChip: {
      style: 'font-size: 12px;',
      VIcon: { size: 'small' },
    },
    VAlert: { style: 'font-size: 13px;' },
    VTextField: {
      density: 'compact',
      color: 'primary',
      bgColor: 'white',
      autocomplete: 'off',
      variant: 'filled',
      rounded: 0,
      flat: true,
      class: 'rtls-text-field',
    },
    VBtn: {
      class: 'rtls-btn',
      VIcon: { color: 'primary', size: 'small' },
    },
    VBtnToggle: {
      border: 'thin opacity-75',
      class: 'rtls-btn-toggle',
    },
    VAvatar: {
      style: 'font-size: 1.2rem;',
      class: 'text-uppercase font-weight-bold rtls-avatar',
      VIcon: { size: '1.2rem' },
    },
    VDatePicker: { firstDayOfWeek: 1 },
    VFileInput: { density: 'compact' },
    VListItem: { minHeight: '32px' },
    VListItemTitle: { style: { fontSize: '1.2rem' } },
  },
  theme: {
    themes: {
      light: {
        // Setting primary app colours for material theme
        colors: {
          primary: '#2f477c',
          accent: '#2f477c',
          success: '#398203',
          background: '#f2f3f4',
          error: owColors.rtlsColors.red70,
        },
      },
    },
  },
  icons: {
    defaultSet: 'mdi',
    sets: {
      md,
      mdi,
      rtls: icons,
    },
    aliases: {
      /* We're aiming to get rid of RTLS Icons in vafor of MDI standard icon set.
      Deprecated RTLS Icons:
      - search -> mdi-magnify
      - <add deprecated RTLS icons here>
      */
      ...Object.keys(rtlsIcons.values).reduce(
        (acc, iconName) => ({ ...acc, [iconName]: rtlsIcons.values[iconName].component }),
        {}
      ),
      unarchive: 'mdi-package-up',
      copy: 'mdi-content-copy',
      check_circle: 'mdi-check-circle',
      expand_more: 'mdi-chevron-down',
    },
  },
});

export { vuetify, pathToComponentName };
